import { ref, watch, computed, reactive } from 'vue'
import { dedupedRequest, ignoreCancelled } from 'gleam-shared/network/http'

import flattenTree from 'gleam-shared/helpers/flattenTree'
import updateQuery from 'gleam-shared/network/updateQuery'
import normalizeUrl from 'gleam-shared/network/normalizeUrl'
import useInProgress from 'gleam-shared/composables/useInProgress'

const DEDUPED_KEY = 'useHelpCenterFrameManager'

export default function useHelpCenterFrameManager(sourceFrameRef, requestedRouteRef) {
  const frame = reactive({})
  const errorRef = ref(null)
  const currentRouteRef = ref(null)
  const flatCategoriesRef = computed(flattenCategories)
  const [fetchInProgress, fetchWithProgress] = useInProgress(fetch)

  watch(sourceFrameRef, onSourceFrameChange, { immediate: true })
  watch(requestedRouteRef, onRequestedRouteChange, { immediate: true })

  return [errorRef, frame, currentRouteRef, fetchInProgress]

  function flattenCategories() {
    return flattenTree(frame.categories, 'categories')
  }

  function onSourceFrameChange(sourceFrame) {
    replaceFrame(sourceFrame)
  }

  function onRequestedRouteChange(requestedRoute) {
    if (isDifferentType(requestedRoute)) {
      return processTypeChange(requestedRoute)
    } else if (isDifferentLocation(requestedRoute)) {
      return processLocationChange(requestedRoute)
    } else {
      replaceCurrentRoute(requestedRoute)
    }
  }

  function processTypeChange(requestedRoute) {
    if (requestedRoute.state) {
      replaceFrame(requestedRoute.state)
      replaceCurrentRoute(requestedRoute)
    } else {
      return fetchWithProgress(appendFrameParam(requestedRoute)).then((frame) => {
        replaceFrame(frame)
        replaceCurrentRoute(requestedRoute)
      }, (error) => {
        errorRef.value = error
        console.warn('Couldn\'t fetch a frame', error)
      })
    }
  }

  function processLocationChange(requestedRoute) {
    const matchingTab = findMatchingTab(requestedRoute)
    const matchingCategory = findMatchingCategory(requestedRoute)

    if (requestedRoute.state) {
      frame.post = requestedRoute.state.post
      frame.paginated_posts = requestedRoute.state.paginated_posts
      frame.selected_category = requestedRoute.state.selected_category
      replaceCurrentRoute(requestedRoute)
    } else if (matchingTab) {
      frame.post = null
      frame.selected_category = null
      replaceCurrentRoute(requestedRoute)
    } else if (matchingCategory?.paginate_url) {
      return fetchWithProgress(matchingCategory.paginate_url).then((paginatedPosts) => {
        frame.post = null
        frame.paginated_posts = paginatedPosts
        frame.selected_category = requestedRoute.pathname
        replaceCurrentRoute(requestedRoute)
      }, (error) => {
        errorRef.value = error
        console.warn('Couldn\'t fetch paginated posts', error)
      })
    } else if (matchingCategory) {
      frame.post = null
      frame.selected_category = requestedRoute.pathname
      replaceCurrentRoute(requestedRoute)
    } else {
      return fetchWithProgress(requestedRoute.href).then((post) => {
        frame.post = post
        replaceCurrentRoute(requestedRoute)
      }, (error) => {
        errorRef.value = error
        console.warn('Couldn\'t fetch a post', error)
      })
    }
  }

  function isDifferentType(requestedRoute) {
    if (!currentRouteRef.value) {
      return false
    }

    return requestedRoute.type !== currentRouteRef.value.type
  }

  function isDifferentLocation(requestedRoute) {
    if (!currentRouteRef.value) {
      return false
    }

    const diffPath = requestedRoute.pathname !== currentRouteRef.value.pathname
    const diffSearch = requestedRoute.search !== currentRouteRef.value.search
    return diffPath || diffSearch
  }

  function findMatchingTab(requestedRoute) {
    return frame.tabs.find((tab) => {
      return normalizeUrl(tab.url) === normalizeUrl(requestedRoute.pathname)
    })
  }

  function findMatchingCategory(requestedRoute) {
    return flatCategoriesRef.value.find((category) => {
      return normalizeUrl(category.url) === normalizeUrl(requestedRoute.pathname)
    })
  }

  function appendFrameParam(requestedRoute) {
    return updateQuery(requestedRoute.href, { frame: true })
  }

  function appendJsonParam(url) {
    return updateQuery(url, { json: true })
  }

  function fetch(url) {
    return dedupedRequest(appendJsonParam(url), DEDUPED_KEY).catch(ignoreCancelled((error) => {
      return Promise.reject(error)
    }))
  }

  function replaceFrame(newFrame) {
    if (!newFrame) {
      console.warn('Unexpected empty frame is given!')
      return
    }

    for (const key of Object.keys(newFrame)) {
      if (key === 'tabs' && frame.tabs) {
        continue
      }

      frame[key] = newFrame[key]
    }
  }

  function replaceCurrentRoute(newRoute) {
    currentRouteRef.value = newRoute
  }
}
